import { useMemo, useState, useEffect } from 'react';
import { fmt, fmtUSD, computeDerived, buildForecast, ageAt, yearAtAge, CLASS_COLOR } from '../lib/portfolio';
import { memberLabel } from '../lib/theme';
import { glassCard, pageBg, cell, th, labelCell, sectionTitle, sectionSub } from '../lib/ui';
import UpdateActualsModal from '../components/actuals/UpdateActualsModal';
import { apiGet } from '../lib/api';
import { Pencil } from 'lucide-react';

export default function Actual({ data, members, savedPlan, fcPrefs = {}, saveFcPrefs }) {
  const plannedMonthly = savedPlan?.monthlyBase ?? (data?.user_id === 'golu' ? 100000 : 200000);
  const meta = data?.asset_meta || [];
  const entities = data?.entity_order || ['PRANAV', 'RASHU', 'HUF'];
  const usdInr = data?.usd_inr || 84;
  const [editOpen, setEditOpen] = useState(false);
  const [allHoldings, setAllHoldings] = useState([]);
  const [monthlyInv, setMonthlyInv] = useState(200000);
  const [selMonth, setSelMonth] = useState(null); // null = latest

  // full history for the editor + month selector
  useEffect(() => {
    apiGet('/data').then(r => r.json())
      .then(d => setAllHoldings(d.holdings || [])).catch(() => {});
  }, []);

  const monthsList = useMemo(
    () => [...new Set(allHoldings.map(h => h.month_ending))].sort().reverse(),
    [allHoldings]
  );
  const viewMonth = selMonth || data?.latest_month;

  // matrix for the selected month (from full history); fall back to dashboard's latest matrix
  const matrix = useMemo(() => {
    if (!viewMonth) return data?.matrix;
    if (viewMonth === data?.latest_month && allHoldings.length === 0) return data?.matrix;
    const m = {}; entities.forEach(e => { m[e] = {}; meta.forEach(a => { m[e][a.asset] = 0; }); });
    allHoldings.filter(h => h.month_ending === viewMonth).forEach(h => {
      const col = h.asset === 'STOCKS_MF' ? 'STOCKS' : h.asset;
      // US stocks: re-price USD holding at the live rate (matches dashboard)
      const val = (h.asset === 'US_STOCKS' && h.usd_value != null) ? h.usd_value * usdInr : h.value;
      if (m[h.entity] && m[h.entity][col] !== undefined) m[h.entity][col] += val;
    });
    return m;
  }, [viewMonth, allHoldings, data, meta, entities, usdInr]);

  const derived = useMemo(
    () => matrix ? computeDerived(matrix, meta, entities, null) : null,
    [matrix, meta, entities]
  );
  const latestDerived = useMemo(
    () => data?.matrix ? computeDerived(data.matrix, meta, entities, null) : null,
    [data, meta, entities]
  );

  if (!data || !derived || data.empty) {
    return (
      <div className="min-h-screen px-4 lg:px-8 py-6 lg:py-8" style={pageBg}>
        <div className="flex flex-col items-center justify-center gap-4" style={{ height: '60vh' }}>
          <p style={{ color: 'var(--text-dim)', fontSize: 16 }}>No holdings yet. Add your latest month to get started.</p>
          <button onClick={() => setEditOpen(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
            style={{ background: 'var(--accent-grad)', color: '#fff' }}><Pencil size={15} /> Add This Month</button>
        </div>
        <UpdateActualsModal open={editOpen} onClose={() => setEditOpen(false)}
          data={{ ...data, _allHoldings: allHoldings }} members={members}
          onSaved={() => window.location.reload()} />
      </div>
    );
  }

  const fmtMonth = iso => iso ? new Date(iso + 'T00:00:00Z').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : '';

  // Rebalance vs saved plan (only for the latest month — comparing past months to a plan is meaningless).
  // Targets are plan's allocation % applied to TODAY's networth, so a market move that scales the whole
  // pie up/down doesn't fake buy/sell signals — only real allocation skew shows.
  const planned = savedPlan?.planned;
  const showRebal = planned && Object.keys(planned).length > 0 && viewMonth === data.latest_month;
  const driftTH = 25000; // flat ₹25k floor — shows real per-cell trades, ignores FX jitter
  const plannedNW = showRebal ? entities.reduce((s, e) => s + meta.reduce((ss, m) => ss + (planned[e]?.[m.asset] || 0), 0), 0) : 0;
  const rebalScale = plannedNW > 0 ? derived.networth / plannedNW : 1;
  const baseline = savedPlan?.baseline; // present on plans saved after the baseline fix; undefined on older plans
  const cellDelta = (e, a) => {
    if (!showRebal) return null;
    // Suppress buy/sell on cells the user did not intentionally reallocate at
    // plan-save time. Without a baseline (legacy plans) fall back to full drift.
    if (baseline && baseline[e]?.[a] === planned[e]?.[a]) return null;
    const target = (planned[e]?.[a] || 0) * rebalScale;
    const d = target - (matrix[e]?.[a] || 0);
    if (Math.abs(d) < driftTH) return null;
    return <span style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginTop: '2px', color: d > 0 ? 'var(--pos)' : 'var(--neg)' }}>
      {d > 0 ? `▲ Buy ${fmt(d)}` : `▼ Sell ${fmt(-d)}`}
    </span>;
  };
  // Net-funding gap stays as plan vs actual NW (this is the "do you need more capital" hint,
  // which is distinct from per-cell rebalance; left unchanged).
  const netFunding = plannedNW - derived.networth;

  return (
    <div className="min-h-screen px-4 lg:px-8 py-6 lg:py-8" style={pageBg}>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-display" style={{ color: 'var(--text)' }}>Actual Holdings</h1>
          <p className="text-base mt-1" style={{ color: 'var(--text-dim)' }}>Your real position — pick any month</p>
        </div>
        <div className="flex items-center gap-3">
          {monthsList.length > 0 && (
            <select value={viewMonth || ''} onChange={e => setSelMonth(e.target.value)}
              className="px-4 py-2.5 rounded-xl text-base font-medium outline-none"
              style={{ background: 'var(--surface)', border: '1px solid var(--surface-border)', color: 'var(--text)', colorScheme: 'dark' }}>
              {monthsList.map(m => <option key={m} value={m}>{fmtMonth(m)}</option>)}
            </select>
          )}
          <button onClick={() => setEditOpen(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-base font-semibold transition-all active:scale-95"
            style={{ background: 'var(--accent-grad)', color: '#fff', boxShadow: '0 4px 16px var(--accent-glow)' }}>
            <Pencil size={16} /> Add This Month
          </button>
        </div>
      </div>

      <UpdateActualsModal open={editOpen} onClose={() => setEditOpen(false)}
        data={{ ...data, _allHoldings: allHoldings }} members={members}
        onSaved={() => window.location.reload()} />

      <div style={{ ...glassCard, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <span style={sectionTitle}>Holdings by Member & Asset</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
            <thead>
              <tr style={{ background: 'var(--head-bg)' }}>
                <th style={{ ...th, textAlign: 'left' }}>Member</th>
                {meta.map(m => <th key={m.asset} style={th}>{m.label}</th>)}
                <th style={{ ...th, color: '#fb923c' }}>Networth</th>
              </tr>
              <tr style={{ background: 'var(--head-2)' }}>
                <td style={{ ...labelCell, color: 'var(--text-mute)', fontSize: '13px' }}>Asset CAGR</td>
                {meta.map(m => <td key={m.asset} style={{ ...cell, color: 'var(--pos)', fontSize: '14px' }}>{(m.cagr * 100).toFixed(1)}%</td>)}
                <td style={cell}></td>
              </tr>
              <tr style={{ background: 'var(--head-2)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <td style={{ ...labelCell, color: 'var(--text-mute)', fontSize: '13px' }}>Class</td>
                {meta.map(m => <td key={m.asset} style={{ ...cell, color: CLASS_COLOR[m.class], fontSize: '13px' }}>{m.class}</td>)}
                <td style={cell}></td>
              </tr>
            </thead>
            <tbody>
              {entities.map(e => (
                <tr key={e} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ ...labelCell, color: 'var(--text)', fontWeight: 600 }}>{memberLabel(e, members)}</td>
                  {meta.map(m => {
                    const v = matrix[e]?.[m.asset] || 0;
                    if (m.asset === 'US_STOCKS') {
                      return <td key={m.asset} style={{ ...cell, verticalAlign: 'top', color: v ? 'var(--text)' : 'var(--text-mute)' }}>
                        {v ? <>{fmtUSD(v, usdInr)}<span style={{ display: 'block', fontSize: '11px', color: 'var(--text-mute)' }}>{fmt(v)}</span></> : '–'}
                        {cellDelta(e, m.asset)}
                      </td>;
                    }
                    return <td key={m.asset} style={{ ...cell, verticalAlign: 'top', color: v ? 'var(--text)' : 'var(--text-mute)' }}>{v ? fmt(v) : '–'}{cellDelta(e, m.asset)}</td>;
                  })}
                  <td style={{ ...cell, color: '#fb923c', fontWeight: 700 }}>{fmt(derived.entityTotals[e])}</td>
                </tr>
              ))}
              <tr style={{ background: 'rgba(249,115,22,0.07)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                <td style={{ ...labelCell, color: '#fb923c', fontWeight: 700 }}>TOTAL</td>
                {meta.map(m => <td key={m.asset} style={{ ...cell, color: 'var(--text)', fontWeight: 600 }}>{fmt(derived.assetTotals[m.asset])}</td>)}
                <td style={{ ...cell, color: '#fb923c', fontWeight: 700 }}>{fmt(derived.networth)}</td>
              </tr>
              <tr style={{ background: 'var(--head-2)' }}>
                <td style={{ ...labelCell, color: 'var(--text-dim)', fontSize: '14px' }}>Allocation %</td>
                {meta.map(m => {
                  const pct = derived.networth ? (derived.assetTotals[m.asset] / derived.networth) * 100 : 0;
                  return <td key={m.asset} style={{ ...cell, color: 'var(--text-dim)', fontSize: '14px' }}>{pct.toFixed(1)}%</td>;
                })}
                <td style={{ ...cell, color: 'var(--text-dim)', fontSize: '14px' }}>100%</td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* Summary strip */}
        <div className="flex flex-wrap items-end gap-8 px-6 py-5" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div>
            <p style={{ fontSize: '13px', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Networth</p>
            <p className="font-display" style={{ fontSize: '30px', fontWeight: 700, color: 'var(--text)' }}>{fmt(derived.networth)}</p>
            <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--pos)' }}>{fmtUSD(derived.networth, usdInr)}</p>
          </div>
          <div>
            <p style={{ fontSize: '13px', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Blended CAGR</p>
            <p className="font-display" style={{ fontSize: '30px', fontWeight: 700, color: 'var(--pos)' }}>{(derived.blendedCagr * 100).toFixed(2)}%</p>
          </div>
          {['EQUITY', 'DEBT', 'CASH'].map(c => (
            <div key={c}>
              <p style={{ fontSize: '13px', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c}</p>
              <p className="font-display" style={{ fontSize: '22px', fontWeight: 700, color: CLASS_COLOR[c] }}>{derived.classPct[c].toFixed(1)}%</p>
            </div>
          ))}
          {showRebal && (
            <div className="ml-auto text-right">
              <p style={{ fontSize: '13px', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>To reach your plan</p>
              <p className="font-display" style={{ fontSize: '22px', fontWeight: 700, color: Math.abs(netFunding) < driftTH ? 'var(--pos)' : '#fbbf24' }}>
                {Math.abs(netFunding) < driftTH ? 'Balanced — rebalance only' : (netFunding > 0 ? `Add ${fmt(netFunding)} new` : `Free up ${fmt(-netFunding)}`)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Independent forecast — driven by ACTUALS blended CAGR */}
      <ActualForecast derived={latestDerived || derived} startDate={data.latest_month} usdInr={usdInr} dob={data.dob} memberName={memberLabel('PRANAV', members)} plannedMonthly={plannedMonthly} plannedSchedule={savedPlan?.monthlySchedule || {}} fcPrefs={fcPrefs} saveFcPrefs={saveFcPrefs} />
    </div>
  );
}

function ActualForecast({ derived, startDate, usdInr, dob, memberName, plannedMonthly = 200000, plannedSchedule = {}, fcPrefs = {}, saveFcPrefs }) {
  const cagr = derived.blendedCagr;
  const start = startDate || new Date().toISOString().slice(0, 10);
  const [monthly, setMonthly] = useState(plannedMonthly); // defaults to planned monthly from Planning tab
  const [monthlyTouched, setMonthlyTouched] = useState(false);
  // keep in sync with Planning's saved monthly until the user overrides it here
  useEffect(() => { if (!monthlyTouched) setMonthly(plannedMonthly); }, [plannedMonthly, monthlyTouched]);
  // Forecast window: fixed 5-yr slice. Dropdown picks which 5-yr window from start
  // forward (offset 0 = current..+5yr). Persisted in fcPrefs.actFcOffset (years).
  const FORECAST_WINDOW_YEARS = 5;
  const [forecastOffset, setForecastOffset] = useState(() => Number(fcPrefs.actFcOffset) || 0);
  const setForecastOffsetPersist = v => { setForecastOffset(v); saveFcPrefs?.({ actFcOffset: v }); };
  const monthYearLabel = ymd => {
    const d = new Date(ymd + 'T00:00:00Z');
    return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  };
  const addYears = (ymd, yrs) => {
    const d = new Date(ymd + 'T00:00:00Z');
    d.setUTCFullYear(d.getUTCFullYear() + yrs);
    return d.toISOString().slice(0, 10);
  };
  const forecastOptions = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45].map(off => ({
    off,
    label: `${monthYearLabel(addYears(start, off))} – ${monthYearLabel(addYears(start, off + FORECAST_WINDOW_YEARS))}`,
  }));
  // corpus growth + monthly contributions over 50 years
  // mirror Planning: base monthly + the saved per-month schedule overrides
  const rows = buildForecast({ startDate: start, opening: derived.networth, monthlyBase: monthly, monthlySchedule: plannedSchedule, annualCagr: cagr, months: 600 });
  const startYear = Number(start.slice(0, 4));
  const [pickYear, setPickYear] = useState(() => fcPrefs.actFc || 2030);
  const [pickYear2, setPickYear2] = useState(() => fcPrefs.actAge || yearAtAge(dob, 60) || 2051);
  const stepFc = fn => setPickYear(y => { const n = fn(y); saveFcPrefs?.({ actFc: n }); return n; });
  const stepAge = fn => setPickYear2(y => { const n = fn(y); saveFcPrefs?.({ actAge: n }); return n; });

  const valAtYear = y => { const r = [...rows].reverse().find(x => new Date(x.date + 'T00:00:00Z').getUTCFullYear() === y); return r ? r.closing : null; };
  const hit100 = rows.find(x => x.closing >= 1e9); // 100 Cr
  const hit100Label = hit100 ? new Date(hit100.date + 'T00:00:00Z').toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : null;
  const ageNow = dob ? ageAt(dob, start) : null;

  const c = { padding: '12px 16px', fontFamily: 'ui-monospace,monospace', fontSize: '14px', textAlign: 'right' };
  const hd = { ...c, color: 'var(--text-dim)', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, fontFamily: 'Inter,sans-serif' };
  const card = { ...glassCard, padding: '18px 22px', flex: '1 1 220px' };
  const lbl = { fontSize: '12px', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' };
  const big = { fontSize: '24px', fontWeight: 700, color: 'var(--text)' };
  const v2030 = valAtYear(pickYear), v60 = valAtYear(pickYear2);
  const age2 = dob ? ageAt(dob, `${pickYear2}-12-31`) : null;

  return (
    <div className="mt-5">
      <div className="flex flex-wrap gap-4 mb-4">
        <div style={card}>
          <p style={lbl} className="flex items-center gap-2">
            Forecast in
            <span className="inline-flex items-center gap-1">
              <button onClick={() => stepFc(y => Math.max(startYear, y - 1))} style={{ color: 'var(--accent)', fontWeight: 700 }}>‹</button>
              <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{pickYear}</span>
              <button onClick={() => stepFc(y => y + 1)} style={{ color: 'var(--accent)', fontWeight: 700 }}>›</button>
            </span>
          </p>
          <p className="font-display" style={big}>{v2030 ? fmt(v2030) : '—'}</p>
          <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--pos)' }}>{v2030 ? fmtUSD(v2030, usdInr) : ''}</p>
        </div>
        <div style={card}>
          <p style={lbl} className="flex items-center gap-2">
            {memberName} at
            <span className="inline-flex items-center gap-1">
              <button onClick={() => stepAge(y => Math.max(startYear, y - 1))} style={{ color: 'var(--accent)', fontWeight: 700 }}>‹</button>
              <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{pickYear2}</span>
              <button onClick={() => stepAge(y => y + 1)} style={{ color: 'var(--accent)', fontWeight: 700 }}>›</button>
            </span>
            {age2 != null && <span style={{ color: 'var(--text-mute)' }}>· age {age2}</span>}
          </p>
          <p className="font-display" style={big}>{v60 ? fmt(v60) : '—'}</p>
          <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--pos)' }}>{v60 ? fmtUSD(v60, usdInr) : ''}</p>
        </div>
        <div style={card}>
          <p style={lbl}>₹100 Cr milestone</p>
          <p className="font-display" style={{ ...big, color: hit100 ? 'var(--accent)' : 'var(--text-mute)' }}>{hit100Label || '> 50 yrs'}</p>
          <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-dim)' }}>{hit100 && ageNow != null ? `${memberName} aged ${ageAt(dob, hit100.date)}` : ''}</p>
        </div>
      </div>

      <div style={{ ...glassCard, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--divider)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <span style={sectionTitle}>Forecast (from Actuals)</span>
            <span style={sectionSub}>@ {(cagr * 100).toFixed(2)}% blended CAGR · +{fmt(monthly)}/mo · 5-yr window</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span style={{ fontSize: '12px', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Period</span>
            <select value={forecastOffset} onChange={e => setForecastOffsetPersist(Number(e.target.value))}
              className="px-3 py-1.5 rounded-lg text-sm outline-none"
              style={{ background: 'var(--surface)', border: '1px solid var(--surface-border)', color: 'var(--text)', colorScheme: 'dark', minWidth: '180px' }}>
              {forecastOptions.map(o => <option key={o.off} value={o.off}>{o.label}</option>)}
            </select>
            <span style={{ fontSize: '12px', color: 'var(--text-mute)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Monthly Inv.</span>
            <input type="number" value={monthly} onChange={e => { setMonthlyTouched(true); setMonthly(Math.max(0, Number(e.target.value) || 0)); }}
              className="px-3 py-1.5 rounded-lg text-right text-sm outline-none"
              style={{ background: 'var(--surface)', border: '1px solid var(--surface-border)', color: 'var(--text)', fontFamily: 'monospace', width: '140px', colorScheme: 'dark' }} />
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
            <thead>
              <tr style={{ background: 'var(--head-bg)' }}>
                <th style={{ ...hd, textAlign: 'left' }}>Date</th><th style={hd}>Age</th><th style={hd}>Opening</th><th style={hd}>Monthly Inv.</th><th style={hd}>Interest</th><th style={hd}>Closing</th><th style={hd}>Closing (USD)</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(forecastOffset * 12, (forecastOffset + FORECAST_WINDOW_YEARS) * 12).map((row, i) => (
                <tr key={row.date} style={{ background: i % 2 ? 'var(--row-alt)' : 'transparent', borderBottom: '1px solid var(--divider)' }}>
                  <td style={{ ...c, textAlign: 'left', color: 'var(--text-dim)', whiteSpace: 'nowrap', fontFamily: 'Inter,sans-serif' }}>{row.label}</td>
                  <td style={{ ...c, color: 'var(--text-mute)' }}>{dob ? ageAt(dob, row.date) : '–'}</td>
                  <td style={{ ...c, color: 'var(--text-mute)' }}>{fmt(row.opening)}</td>
                  <td style={{ ...c, color: 'var(--text-dim)' }}>{row.monthly ? fmt(row.monthly) : '–'}</td>
                  <td style={{ ...c, color: 'var(--pos)' }}>{fmt(row.interest)}</td>
                  <td style={{ ...c, color: 'var(--text)', fontWeight: 700 }}>{fmt(row.closing)}</td>
                  <td style={{ ...c, color: 'var(--accent)' }}>{fmtUSD(row.closing, usdInr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
